import { ArrowDown, ArrowUp, Eye, EyeOff, Tag, Trash2 } from 'react-feather';
import { FormManager } from 'react-form-state-manager';
import { Fragment, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import keyBy from 'lodash/keyBy';
import styled, { css } from 'styled-components';

import { Locale, ModelType } from '__generated__/graphql';

import { ErrorBag } from '../../common/helpers';
import { TranslatableInput } from '../Translations/Inputs';
import DateRangeForm from './DateRangeForm';
import UI from '../../common/UI';
import useCurrencyInput from '../../common/useCurrencyInput';
import useIntegerInput from '../../common/useIntegerInput';
import useLocale from '../../common/useLocale';
import useMediaDevice from '../../common/useMediaDevice';

export interface PromotionData {
  id?: string | null;
  title?: string | null;
  description?: string | null;
  capacity?: number | null;
  amount?: number | null;
  start_date?: string | null;
  end_date?: string | null;
  standard?: boolean;
  restricted_to_tickets?: { id: string }[];
  restricted_to_promotions?: { id: string }[];
}

export interface PricingData {
  vat_percentage?: number;
  promotions: PromotionData[];
}

export interface Promotion {
  id: string;
  standard: boolean;
  invitation_reservations_count: number;
  sold_count: number;
  deletable: boolean;
  locale: Locale;
  translations: {
    attribute: string;
    locale: Locale;
    value: string;
  }[];
  restricted_to_tickets?: {
    title?: string;
  }[];
  restricted_to_promotions?: {
    title?: string;
  }[];
}

interface Ticket {
  id: string;
  title?: string;
  active: boolean;
  promotions: {
    id: string;
    title?: string;
    active: boolean;
  }[];
}

interface PricingFormProps {
  form: FormManager<PricingData>;
  promotions?: Promotion[];
  event?: {
    tickets: Ticket[];
  };
  locale?: Locale;
  isTicket?: boolean;
  errors: ErrorBag;
}

const PricingForm = ({ form, promotions = [], event, locale, isTicket, errors }: PricingFormProps) => {
  const { t } = useTranslation();

  const [multiRate, setMultiRate] = useState(form.values.promotions.length > 1);

  // Generate unique key props for the list of promotion forms
  const promotionFormKeys = useRef(form.values.promotions.map((value, index) => index));

  const addPromotion = () => {
    form.append('promotions', {
      active: true,
      title_translations: [],
      restricted_to_tickets: [],
      restricted_to_promotions: [],
    });
    promotionFormKeys.current.push(Math.max(0, ...promotionFormKeys.current) + 1);
  };

  const deletePromotion = (index: number) => {
    form.delete('promotions', index);
    promotionFormKeys.current.splice(index, 1);
  };

  const movePromotionUp = (index: number) => {
    form.moveUp('promotions', index);

    const key = promotionFormKeys.current[index];
    promotionFormKeys.current[index] = promotionFormKeys.current[index - 1];
    promotionFormKeys.current[index - 1] = key;
  };

  const movePromotionDown = (index: number) => {
    form.moveDown('promotions', index);

    const key = promotionFormKeys.current[index];
    promotionFormKeys.current[index] = promotionFormKeys.current[index + 1];
    promotionFormKeys.current[index + 1] = key;
  };

  const keyedPromotions = keyBy(promotions, 'id');

  return (
    <UI.FormGrid id="PricingForm">
      <UI.InputGroup>
        <UI.RadioPill>
          <UI.Radio
            checked={!multiRate}
            onChange={() => setMultiRate(false)}
            disabled={form.values.promotions.length > 1}
            sc={{ box: true, fontWeight: 500 }}
          >
            {t('single_rate')}
          </UI.Radio>
          <UI.Radio
            checked={multiRate}
            onChange={() => setMultiRate(true)}
            sc={{ box: true, fontWeight: 500 }}
          >
            {t('multiple_rates')}
          </UI.Radio>
        </UI.RadioPill>
      </UI.InputGroup>

      {!multiRate && (
        <SinglePromotionForm
          form={form}
          errors={errors}
          isTicket={isTicket}
          event={event}
        />
      )}

      {multiRate && (
        <>
          {form.values.promotions.map((value, index) => (
            <PromotionForm
              form={form}
              index={index}
              promotion={keyedPromotions[value.id]}
              tickets={event && event.tickets}
              locale={locale}
              errors={errors}
              deletePromotion={() => deletePromotion(index)}
              movePromotionUp={() => movePromotionUp(index)}
              movePromotionDown={() => movePromotionDown(index)}
              isTicket={isTicket}
              key={value.id || promotionFormKeys.current[index]}
            />
          ))}

          <UI.Div>
            <UI.Button
              onClick={() => addPromotion()}
            >
              {t('add_promotion')}
            </UI.Button>
          </UI.Div>
        </>
      )}

      {!isTicket && (
        <UI.InputGroup sc={{ valid: !errors.required, touched: form.touched.required }}>
          <UI.InputLabel htmlFor="RequiredProduct">
            {t('required_product')}
          </UI.InputLabel>
          <UI.Checkbox {...form.checkbox('required')} id="RequiredProduct">
            {t('required_product_label')}
          </UI.Checkbox>
          <UI.ErrorMessages attribute={t('required')} errors={errors.required} />
        </UI.InputGroup>
      )}
    </UI.FormGrid>
  );
};

interface SinglePromotionFormProps {
  form: FormManager<PricingData>;
  errors: ErrorBag;
  isTicket?: boolean;
  event?: {
    tickets: Ticket[];
  };
}

export const SinglePromotionForm = ({ form, errors, isTicket, event }: SinglePromotionFormProps) => {
  const { t } = useTranslation();
  const currencyInput = useCurrencyInput();

  const standardPromotionIndex = 0;
  const key = (k?: string) => `promotions.${standardPromotionIndex}${k ? `.${k}` : ''}`;

  return (
    <>
      <UI.FormGrid sc={{ columns: '1fr 1fr' }}>
        <UI.InputGroup>
          <UI.InputLabel htmlFor="PromotionAmount_0">
            {t('selling_price')}
          </UI.InputLabel>
          <UI.CurrencyInput
            {...form.text(key('amount'), {
              inputHandler: {
                parse: currencyInput.parse,
                format: currencyInput.value,
              },
            })}
            id="PromotionAmount_0"
          />
          <UI.ErrorMessages
            attribute={t('selling_price')}
            errors={errors[key('amount')]}
          />
        </UI.InputGroup>
      </UI.FormGrid>

      <DateRangeForm
        form={form}
        errors={errors}
        keys={{
          startDate: key('start_date'),
          startTime: key('start_time'),
          endDate: key('end_date'),
          endTime: key('end_time'),
        }}
        labels={{
          startDate: t('start_of_sales'),
          endDate: t('end_of_sales'),
        }}
      />

      {!isTicket && (
        <RestrictedToTicketsForm
          form={form}
          index={0}
          errors={errors}
          tickets={event.tickets}
        />
      )}
    </>
  );
};

interface PromotionFormProps {
  form: FormManager<PricingData>;
  index: number;
  promotion?: Promotion;
  tickets?: Ticket[];
  locale?: Locale;
  isTicket?: boolean;
  errors: ErrorBag;
  deletePromotion: () => void;
  movePromotionUp: () => void;
  movePromotionDown: () => void;
}

const PromotionForm = ({
  form,
  index,
  promotion,
  tickets,
  locale,
  isTicket,
  errors,
  deletePromotion,
  movePromotionDown,
  movePromotionUp,
}: PromotionFormProps) => {
  const { t } = useTranslation();
  const { formatInteger } = useLocale();
  const currencyInput = useCurrencyInput();
  const integerInput = useIntegerInput();

  const device = useMediaDevice();

  const key = (k?: string) => `promotions.${index}${k ? `.${k}` : ''}`;

  const sellingPriceLabel = t('selling_price');

  const standard = index === 0;

  const purchasesCount = promotion
    && t('purchases_count', {
      value: formatInteger(promotion.sold_count),
      count: promotion.sold_count,
    });

  const reservationsCount = promotion && promotion.invitation_reservations_count > 0
    && t('reservations_count', {
      value: formatInteger(promotion.invitation_reservations_count),
      count: promotion.invitation_reservations_count,
    });

  const hasRestrictions = form.values.promotions[index].restricted_to_tickets?.length > 0;
  const [showRestrictions, setShowRestrictions] = useState(hasRestrictions);
  const toggleRestrictions = () => {
    form.set(key('restricted_to_tickets'), []);
    form.set(key('restricted_to_promotions'), []);
    setShowRestrictions(!showRestrictions);
  };

  const togglePromotion = () => {
    form.set(key('active'), (active: boolean) => !active);
  };

  return (
    <UI.Card sc={{ outline: 'gray.200', hasPanels: true, borderRadius: 'lg' }}>
      <UI.FlexContainer
        sc={{ justifyContent: 'space-between', alignItems: 'center', background: 'gray.50', py: 3 }}
      >
        <UI.Div>
          <UI.Legend sc={{ fontWeight: 500 }}>
            <UI.Icon>
              {form.get(key('active')) ? <Eye /> : <EyeOff />}
            </UI.Icon>
            {` ${t('promotion')} ${index + 1}`}
          </UI.Legend>
          {device.isSmall && purchasesCount && (
            <UI.Div sc={{ small: true }}>
              <UI.Delimit>
                {purchasesCount}
                {reservationsCount}
              </UI.Delimit>
            </UI.Div>
          )}
        </UI.Div>
        <UI.FlexContainer sc={{ alignItems: 'center', my: -0.75 }}>
          {!device.isSmall && purchasesCount && (
            <>
              <UI.Div sc={{ small: true, mr: 2 }}>
                {purchasesCount}
              </UI.Div>
              {reservationsCount && (
                <UI.Div sc={{ small: true, mr: 2 }}>
                  {purchasesCount}
                </UI.Div>
              )}
            </>
          )}
          <UI.Button
            onClick={() => movePromotionUp()}
            sc={{ blank: true, color: 'gray.500', size: 'sm' }}
            disabled={index === 0}
          >
            <UI.Icon>
              <ArrowUp />
            </UI.Icon>
          </UI.Button>
          <UI.Button
            onClick={() => movePromotionDown()}
            sc={{ blank: true, color: 'gray.500', size: 'sm' }}
            disabled={index === form.values.promotions.length - 1}
          >
            <UI.Icon>
              <ArrowDown />
            </UI.Icon>
          </UI.Button>
          <UI.Button
            onClick={() => deletePromotion()}
            sc={{ blank: true, color: 'gray.500', size: 'sm' }}
            disabled={promotion && !promotion.deletable}
          >
            <UI.Icon>
              <Trash2 />
            </UI.Icon>
          </UI.Button>
        </UI.FlexContainer>
      </UI.FlexContainer>

      <UI.Div sc={{ padding: 0 }} style={{ position: 'relative' }}>
        <UI.Div
          sc={{ padding: [3, 4] }}
          style={{
            width: !showRestrictions || device.isSmall ? '100%' : '66.667%',
          }}
        >
          <UI.FormGrid>
            <UI.InputGroup sc={{ valid: !errors[key('title')], touched: form.getTouched(key('title')) }}>
              <UI.InputLabel htmlFor={`PromotionTitle_${index}`}>
                {t('promotion_title')}
                {' '}
                {!standard && <UI.RequiredMark />}
              </UI.InputLabel>
              <TranslatableInput
                form={form}
                errors={errors}
                name={key('title')}
                defaultLocale={promotion?.locale || locale}
                title={t('promotion_title')}
                maxLength={50}
                placeholder={
                  t(standard ? 'standard_rate' : 'promotion_title_placeholder')
                }
                id={`PromotionTitle_${index}`}
              />
              <UI.ErrorMessages attribute={t('promotion_title')} errors={errors[key('title')]} />
            </UI.InputGroup>

            <UI.FormGrid sc={{ columns: ['1fr', '1fr 1fr'] }}>
              <UI.InputGroup>
                <UI.InputLabel htmlFor={`PromotionAmount_${index}`}>
                  {sellingPriceLabel}
                </UI.InputLabel>
                <UI.CurrencyInput
                  {...form.text(key('amount'), {
                    inputHandler: {
                      parse: currencyInput.parse,
                      format: currencyInput.value,
                    },
                  })}
                  id={`PromotionAmount_${index}`}
                />
                <UI.ErrorMessages attribute={sellingPriceLabel} errors={errors[key('amount')]} />
              </UI.InputGroup>

              <UI.InputGroup
                sc={{ valid: !errors[key('capacity')], touched: form.getTouched(key('capacity')) }}
              >
                <UI.InputLabel htmlFor={`PromotionCapacity_${index}`}>
                  {t('capacity')}
                </UI.InputLabel>
                <UI.DebouncedInput
                  {...form.text(key('capacity'), {
                    inputHandler: integerInput(),
                  })}
                  maxLength={10}
                  format={integerInput().format}
                  inputMode="numeric"
                  match={/[\d]/g}
                  placeholder="∞"
                  id={`PromotionCapacity_${index}`}
                />
                <UI.ErrorMessages attribute={t('capacity')} errors={errors[key('capacity')]} />
              </UI.InputGroup>
            </UI.FormGrid>

            <DateRangeForm
              form={form}
              errors={errors}
              keys={{
                startDate: key('start_date'),
                startTime: key('start_time'),
                endDate: key('end_date'),
                endTime: key('end_time'),
              }}
              labels={{
                startDate: t('start_of_sales'),
                endDate: t('end_of_sales'),
              }}
            />

            <UI.InputGroup sc={{ valid: !errors[key('active')], touched: form.getTouched(key('active')) }}>
              {!isTicket && (
                <UI.Checkbox
                  onChange={() => toggleRestrictions()}
                  checked={showRestrictions}
                >
                  {t('restrict_promotion_to_specific_tickets')}
                </UI.Checkbox>
              )}
              <UI.Checkbox
                onChange={() => togglePromotion()}
                checked={!form.get(key('active'))}
              >
                {isTicket ? t('hidden_ticket_rate_description') : t('hidden_product_rate_description')}
              </UI.Checkbox>
              <UI.ErrorMessages
                attribute={t('hidden_rate')}
                errors={errors[key('active')]}
              />
            </UI.InputGroup>
          </UI.FormGrid>
        </UI.Div>

        {!isTicket && showRestrictions && (
          <Sidebar>
            <UI.Div>
              <RestrictedToTicketsForm
                form={form}
                index={index}
                errors={errors}
                tickets={tickets}
              />
            </UI.Div>
          </Sidebar>
        )}
      </UI.Div>
    </UI.Card>
  );
};

interface RestrictedToTicketsFormProps {
  form: FormManager<PricingData>;
  index: number;
  errors: ErrorBag;
  tickets?: Ticket[];
}

export const RestrictedToTicketsForm = ({
  form,
  index,
  errors,
  tickets,
}: RestrictedToTicketsFormProps) => {
  const { t } = useTranslation();
  const promotion = form.values.promotions[index];

  const deselectAllTickets = () => {
    form.set(`promotions.${index}.restricted_to_tickets`, []);
    form.set(`promotions.${index}.restricted_to_promotions`, []);
  };

  const deselectPromotions = (ticket: Ticket) => {
    const promotionIds = ticket.promotions.map(({ id }) => id);

    form.set(`promotions.${index}.restricted_to_promotions`, (values: ModelType[]) => (
      values.filter(({ id }) => !promotionIds.includes(id))
    ));
  };

  const isSelected = (ticketId: string) => promotion.restricted_to_tickets.map(({ id }) => id).includes(ticketId);

  const hasSelectedPromotions = (ticket: Ticket) => {
    const promotionIds = ticket.promotions.map(({ id }) => id);

    return promotion.restricted_to_promotions.filter(({ id }) => promotionIds.includes(id)).length > 0;
  };

  return (
    <UI.InputGroup>
      <UI.InputLabel>
        {t('available_with_tickets')}
      </UI.InputLabel>
      <UI.Checkbox
        checked={promotion.restricted_to_tickets.length === 0}
        disabled={promotion.restricted_to_tickets.length === 0}
        onChange={() => deselectAllTickets()}
      >
        {t('all_tickets')}
      </UI.Checkbox>
      {tickets.map((ticket) => (
        <Fragment key={ticket.id}>
          <UI.Checkbox
            {...form.checklist(`promotions.${index}.restricted_to_tickets`, { id: ticket.id }, {
              key: 'id',
              onChange: (values: ModelType[]) => {
                if (!values.map(({ id }) => id).includes(ticket.id)) {
                  deselectPromotions(ticket);
                }
              },
            })}
            key={ticket.id}
          >
            {ticket.title}
          </UI.Checkbox>
          {isSelected(ticket.id) && ticket.promotions.length > 1 && (
            <UI.Div sc={{ pl: 4 }}>
              <UI.Checkbox
                checked={!hasSelectedPromotions(ticket)}
                disabled={!hasSelectedPromotions(ticket)}
                onChange={() => deselectPromotions(ticket)}
              >
                {t('all_promotions')}
              </UI.Checkbox>
              {ticket.promotions.map((promotion) => (
                <UI.Checkbox
                  {...form.checklist(`promotions.${index}.restricted_to_promotions`, { id: promotion.id }, { key: 'id' })}
                  key={promotion.id}
                >
                  <UI.Icon sc={{ muted: true }}>
                    <Tag />
                  </UI.Icon>
                  {' '}
                  <UI.Span sc={{ muted: !promotion.active }}>
                    {promotion.title || t('standard')}
                  </UI.Span>
                </UI.Checkbox>
              ))}
            </UI.Div>
          )}
        </Fragment>
      ))}
      <UI.ErrorMessages attribute={t('tickets')} errors={errors.restrictedToTickets} />
      <UI.ErrorMessages attribute={t('promotions')} errors={errors.restrictedToPromotions} />
    </UI.InputGroup>
  );
};

const Sidebar = styled(UI.Div)`
  ${({ theme }) => css`
    padding: 0 ${theme.gutter * 0.75}px ${theme.gutter * 0.75}px ${theme.gutter * 0.75}px;

    > ${UI.Div} {
      padding: 0;
    }

    @media ${theme.devices.md} {
      padding: 0 !important;
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      width: 33.333%;
      box-shadow: inset 0 -6px 6px -3px rgba(0, 0, 0, 0.05);
      border-bottom-right-radius: ${theme.borderRadiuses.xl}px;
      border-left: 1px solid ${theme.colors.gray[200]};
      overflow: hidden;

      > ${UI.Div} {
        padding: ${theme.gutter}px;
        overflow-y: auto;
        height: 100%;
        min-width: 259px;
      }
    }
  `}
`;

export default PricingForm;
